import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, BookOpenIcon, MessageSquareIcon, SearchIcon, SendIcon, ShieldAlertIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { EmptyState } from '../ui/EmptyState';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { Toggle } from '../ui/ChoiceGroup';
import { FeatureGate } from '../platform/FeatureStatusBadge';
import { useTickets } from '../../contexts/TicketContext';
import { helpArticles, reportCategories, ticketCategories } from '../../data/tickets';
import type { Ticket, TicketCategory, TicketStatus } from '../../types/tickets';
const STATUS_TONE: Record<TicketStatus, 'ok' | 'warn' | 'brand' | 'neutral' | 'danger'> = {
  Open: 'brand',
  'Awaiting your reply': 'warn',
  'With support': 'brand',
  Escalated: 'danger',
  Resolved: 'ok',
  Closed: 'neutral'
};
function when(at: string): string {
  return new Date(at).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function HelpCentre() {
  const {
    tickets,
    raiseTicket,
    reply
  } = useTickets();
  const [query, setQuery] = useState('');
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const articles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return helpArticles;
    return helpArticles.filter(article => article.title.toLowerCase().includes(needle) || article.summary.toLowerCase().includes(needle) || article.category.toLowerCase().includes(needle));
  }, [query]);
  const active = tickets.find(ticket => ticket.id === openTicketId) ?? null;
  if (active) {
    return <TicketThread ticket={active} onBack={() => setOpenTicketId(null)} onReply={body => reply(active.id, body)} data-id="element-5030" data-mp="1y6uorh3z8j69" />;
  }
  return <div className="space-y-6" data-id="element-5031" data-mp="3q4alagc2zps">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-5032" data-mp="3uwxdciqrz6wf">
        <Panel title="Search help" description="Most answers are here. If none of them fit, raise a ticket and a person will read it." data-id="element-5033" data-mp="3uxp8jsggn2m6">
          <Field label="What do you need help with?" htmlFor="help-search" data-id="element-5034" data-mp="3m3kdewuan50u">
            <div className="relative" data-id="element-5035" data-mp="3h620ywh2i7o3">
              <SearchIcon aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-subtle" data-id="element-5036" data-mp="3h73ow1j6mon8" />
              <Input id="help-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="escrow, inspection fee, verification code" className="pl-11" data-id="element-5037" data-mp="378e3hys2um28" />
            </div>
          </Field>

          {articles.length === 0 ? <div className="mt-5" data-id="element-5038" data-mp="11cjvynbvshim">
              <EmptyState icon={<BookOpenIcon className="h-5 w-5" data-id="element-5040" data-mp="2jbhtyv7l6ja6" />} title="Nothing matches that" description="Raise a ticket instead and describe it in your own words. We would rather answer it than have you guess." action={<Button variant="primary" onClick={() => setComposing(true)} data-id="element-5041" data-mp="2x3xot8qc3b00">
                    Raise a ticket
                  </Button>} data-id="element-5039" data-mp="2jcsywycrpx2v" />
            </div> : <ul className="mt-5 divide-y divide-line" data-id="element-5042" data-mp="25bgjco1irxhi">
              {articles.map(article => <li key={article.id} className="py-4 first:pt-0 last:pb-0" data-id="element-5043" data-mp="29dk6wb1gqsj5">
                  <p className="text-body font-semibold text-ink" data-id="element-5044" data-mp="2ecoh9504iwb2">{article.title}</p>
                  <p className="mt-1 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-5045" data-mp="2eak5k0vzgt08">{article.summary}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-5046" data-mp="3kvd03r0392ne">
                    <Badge plain data-id="element-5047" data-mp="3pq8s4r5brq0i">{article.category}</Badge>
                    {article.featureKey && <FeatureGate featureKey={article.featureKey} inline data-id="element-5048" data-mp="3obpmlhc0uj2p" />}
                  </div>
                </li>)}
            </ul>}
        </Panel>

        <div className="space-y-6" data-id="element-5049" data-mp="16goac050ci3">
          <Panel title="Raise a ticket" data-id="element-5050" data-mp="17jhwrougt1p">
            <p className="max-w-[46ch] text-body-sm text-ink-muted" data-id="element-5051" data-mp="3ta99pl2cq0wn">
              Report a problem, a person, a listing or a transaction. You will get a reference and
              can follow it here.
            </p>
            <Button variant="primary" className="mt-4" icon={<MessageSquareIcon className="h-4 w-4" data-id="element-5053" data-mp="kupkcx4sx2mv" />} onClick={() => setComposing(true)} data-id="element-5052" data-mp="6632annshwry">
              Raise a ticket
            </Button>
          </Panel>

          <Panel title="What support can and cannot see" data-id="element-5054" data-mp="tumal36ua9k1">
            <ul className="space-y-2.5 text-body-sm text-ink" data-id="element-5055" data-mp="tv5h7nr1hbir">
              <li data-id="element-5056" data-mp="owu8jgchd724">They see the ticket, the record you attached, and your handle.</li>
              <li data-id="element-5057" data-mp="18h2xzfcsllga">
                Financial references are masked. An agent sees the last digits, never a full
                identifier or your balance.
              </li>
              <li data-id="element-5058" data-mp="zgwmu04kgxka">
                They cannot open your notes, diary, wellbeing activity or private messages — not for
                any reason, including at your request.
              </li>
              <li data-id="element-5059" data-mp="13kchws0zmpuv">Every time an agent opens a sensitive record it is recorded against their name.</li>
            </ul>
          </Panel>
        </div>
      </div>

      <Panel title="Your tickets" description="Newest first. Anything waiting on you is marked." bodyClassName="p-0 sm:p-0" data-id="element-5060" data-mp="uil93eb5i2ws">
        {tickets.length === 0 ? <div className="px-5 py-8 sm:px-6" data-id="element-5061" data-mp="kss08l2e81w8">
            <EmptyState icon={<MessageSquareIcon className="h-5 w-5" data-id="element-5063" data-mp="6rz2zskpispf" />} title="You have not raised anything" description="Nothing to follow up. Search help above if you have a question." data-id="element-5062" data-mp="6s8vbmt85bya" />
          </div> : <ul className="divide-y divide-line" data-id="element-5064" data-mp="2dhspz6z6sfky">
            {tickets.map(ticket => <li key={ticket.id} data-id="element-5065" data-mp="28o81c5jhgxyf">
                <button type="button" onClick={() => setOpenTicketId(ticket.id)} className="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 py-4 text-left transition-colors duration-150 ease-out hover:bg-canvas sm:px-6" data-id="element-5066" data-mp="23qoam65lmspl">
                  <div className="min-w-0" data-id="element-5067" data-mp="2rgio8pxqslaw">
                    <p className="text-body font-semibold text-ink" data-id="element-5068" data-mp="2rgsbw6vbegdf">{ticket.subject}</p>
                    <p className="cz-num mt-0.5 text-body-sm text-ink-muted" data-id="element-5069" data-mp="2ndcgteyw8o2u">
                      {ticket.reference} · {ticket.category} · opened {when(ticket.openedAt)}
                    </p>
                    {ticket.context.kind !== 'none' && <p className="cz-num mt-0.5 text-caption text-ink-subtle" data-id="element-5070" data-mp="170p2gctd1w7j">
                        About {ticket.context.label} ({ticket.context.reference})
                      </p>}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-5071" data-mp="3nnx1zfqn7nq3">
                    {ticket.priority === 'Urgent' && <Badge tone="danger" icon={<ShieldAlertIcon className="h-4 w-4" data-id="element-5073" data-mp="9rsb485dlmm" />} data-id="element-5072" data-mp="3rmcl38jlsby8">
                        Urgent
                      </Badge>}
                    <Badge tone={STATUS_TONE[ticket.status]} plain={ticket.status === 'Closed'} data-id="element-5074" data-mp="583079evtm27">
                      {ticket.status}
                    </Badge>
                  </div>
                </button>
              </li>)}
          </ul>}
      </Panel>

      <ComposeTicket open={composing} onClose={() => setComposing(false)} onSubmit={input => {
      const ticket = raiseTicket(input);
      setComposing(false);
      setOpenTicketId(ticket.id);
    }} data-id="element-5075" data-mp="j8xbbztbe14k" />
    </div>;
}

/* ------------------------------------------------------------------- thread */

function TicketThread({
  ticket,
  onBack,
  onReply
}: {
  ticket: Ticket;
  onBack: () => void;
  onReply: (body: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const visible = ticket.messages.filter(message => !message.internal);
  return <div className="space-y-5" data-id="element-5076" data-mp="26vjlftc18r5t">
      <Button variant="ghost" icon={<ArrowLeftIcon className="h-4 w-4" data-id="element-5078" data-mp="1j4mg4zeqss3j" />} onClick={onBack} data-id="element-5077" data-mp="26wl5uoexuq07">
        All tickets
      </Button>

      <Panel title={ticket.subject} description={`${ticket.reference} · ${ticket.category}`} action={<Badge tone={STATUS_TONE[ticket.status]} plain={ticket.status === 'Closed'} data-id="element-5080" data-mp="1s6nbfqqf142k">
            {ticket.status}
          </Badge>} data-id="element-5079" data-mp="1e62ruscof480">
        <dl className="grid gap-4 sm:grid-cols-3" data-id="element-5081" data-mp="1n8u8d6kesq6i">
          <div data-id="element-5082" data-mp="33jpvivrq4brh">
            <dt className="text-body-sm text-ink-muted" data-id="element-5083" data-mp="33j6bn9iijdat">Opened</dt>
            <dd className="mt-0.5 text-body font-medium text-ink" data-id="element-5084" data-mp="2ymfvynamejpx">{when(ticket.openedAt)}</dd>
          </div>
          <div data-id="element-5085" data-mp="371lv63u0nn4c">
            <dt className="text-body-sm text-ink-muted" data-id="element-5086" data-mp="3713qqjx7a2lc">Handled by</dt>
            <dd className="mt-0.5 text-body font-medium text-ink" data-id="element-5087" data-mp="3b4ij8a2bsbfo">
              {ticket.assignedTo ?? 'Not yet assigned'}
            </dd>
          </div>
          <div data-id="element-5088" data-mp="2x6lhz0cxfguo">
            <dt className="text-body-sm text-ink-muted" data-id="element-5089" data-mp="323kfztzpo7y5">About</dt>
            <dd className="cz-num mt-0.5 text-body font-medium text-ink" data-id="element-5090" data-mp="323u6dioo4h4d">
              {ticket.context.kind === 'none' ? 'No linked record' : ticket.context.label}
            </dd>
          </div>
        </dl>
        {ticket.context.masked && <p className="mt-3 border-t border-line pt-3 text-body-sm text-ink-muted" data-id="element-5091" data-mp="3g3v62ut75ii9">
            The financial reference on this ticket is masked for the agent handling it. Only finance
            operations can open the full entry, and doing so is recorded.
          </p>}
        {ticket.confidential && <p className="mt-3 text-body-sm text-ink-muted" data-id="element-5092" data-mp="heeiowkqx8iy">
            You asked to stay unnamed to the person reported. They are told a report was made, never
            by whom.
          </p>}
      </Panel>

      {ticket.resolution && <div className="rounded-card border border-ok-100 bg-ok-50 p-4" data-id="element-5093" data-mp="2xrfl2uoupgqy">
          <p className="text-body font-semibold text-ink" data-id="element-5094" data-mp="2jtijfjvjik5f">How this was resolved</p>
          <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-5095" data-mp="2orshq3cka0v9">{ticket.resolution}</p>
        </div>}

      <Panel title="Conversation" bodyClassName="p-0 sm:p-0" data-id="element-5096" data-mp="2a2nyqjxxzsm3">
        <ul className="divide-y divide-line" data-id="element-5097" data-mp="2evh49a18j4z8">
          {visible.map(message => <li key={message.id} className="px-5 py-4 sm:px-6" data-id="element-5098" data-mp="213sqz3f92ago">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-5099" data-mp="215djkni07jbw">
                <p className="text-body-sm font-semibold text-ink" data-id="element-5100" data-mp="255xkm25p773b">{message.authorName}</p>
                {message.author === 'system' && <Badge plain data-id="element-5101" data-mp="1r7g0eef04cq5">Automatic</Badge>}
                <p className="cz-num text-caption text-ink-subtle" data-id="element-5102" data-mp="1w4zfthnfqvjo">{when(message.at)}</p>
              </div>
              <p className="mt-1.5 max-w-[76ch] text-body text-ink" data-id="element-5103" data-mp="1chuus0yr04k8">{message.body}</p>
            </li>)}
        </ul>
        <div className="border-t border-line px-5 py-4 sm:px-6" data-id="element-5104" data-mp="17l4epdmy18yt">
          <Field label="Add a reply" htmlFor="ticket-reply" data-id="element-5105" data-mp="1mh3tt88h09dg">
            <Textarea id="ticket-reply" rows={3} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Anything else that would help them understand it" data-id="element-5106" data-mp="1mehdaj3lqjj3" />
          </Field>
          <Button variant="primary" className="mt-3" icon={<SendIcon className="h-4 w-4" data-id="element-5108" data-mp="2560gor1jfzln" />} disabled={!draft.trim()} onClick={() => {
          onReply(draft.trim());
          setDraft('');
        }} data-id="element-5107" data-mp="1qi5l92w5bh4v">
            Send reply
          </Button>
        </div>
      </Panel>
    </div>;
}

/* ------------------------------------------------------------------ compose */

const CONTEXT_OPTIONS: Record<string, {
  reference: string;
  label: string;
  surface: string;
}[]> = {
  'Housing': [{
    reference: 'LST-2261',
    label: 'Ceeplus Court, Iwaya — 4-bed shared',
    surface: '/housing/lst-2261'
  }, {
    reference: 'APP-4410',
    label: 'Your application — Ceeplus Court',
    surface: '/housing'
  }],
  'Marketplace order': [{
    reference: 'ORD-8821',
    label: 'Engineering maths tutoring — 4 sessions',
    surface: '/marketplace'
  }, {
    reference: 'ORD-8790',
    label: 'CV review — Standard package',
    surface: '/marketplace'
  }],
  'Money and CampaGift': [{
    reference: 'TXN-0•••••41',
    label: 'Knowledge licence purchase',
    surface: '/money'
  }, {
    reference: 'GFT-3•••17',
    label: 'CampaGift — Books, ₦10,000',
    surface: '/money/campagift'
  }],
  'Academic services': [{
    reference: 'SRQ-1182',
    label: 'Transcript request — UNILAG',
    surface: '/services'
  }],
  'Report a listing': [{
    reference: 'LST-2261',
    label: 'Ceeplus Court, Iwaya — 4-bed shared',
    surface: '/housing/lst-2261'
  }],
  'Report a person': [{
    reference: '@tunde.k',
    label: 'Tunde K — marketplace provider',
    surface: '/community/people'
  }, {
    reference: '@amina.b',
    label: 'Amina B — roommate match',
    surface: '/community/people'
  }],
  'Report a transaction': [{
    reference: 'TXN-0•••••41',
    label: 'Knowledge licence purchase',
    surface: '/money'
  }]
};
function ComposeTicket({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    subject: string;
    category: TicketCategory;
    body: string;
    context: Ticket['context'];
    confidential?: boolean;
  }) => void;
}) {
  const [category, setCategory] = useState<TicketCategory>('Account and sign-in');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [reference, setReference] = useState('');
  const [confidential, setConfidential] = useState(false);
  const options = CONTEXT_OPTIONS[category] ?? [];
  const isReport = reportCategories.includes(category);
  const chosen = options.find(option => option.reference === reference) ?? null;
  const ready = subject.trim().length > 2 && body.trim().length > 9;
  return <Modal open={open} onClose={onClose} title="Raise a ticket" description="Tell us what happened. Attach the record it is about so nobody has to guess." footer={<>
          <Button variant="secondary" onClick={onClose} data-id="element-5110" data-mp="12tm0x1d8droi">
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={() => onSubmit({
      subject: subject.trim(),
      category,
      body: body.trim(),
      confidential: isReport ? confidential : undefined,
      context: chosen ? {
        kind: category === 'Report a person' ? 'person' : category === 'Housing' || category === 'Report a listing' ? 'listing' : category === 'Marketplace order' ? 'order' : category === 'Academic services' ? 'request' : 'transaction',
        reference: chosen.reference,
        label: chosen.label,
        surface: chosen.surface,
        masked: chosen.reference.includes('•')
      } : {
        kind: 'none',
        reference: '—',
        label: 'No linked record',
        surface: ''
      }
    })} data-id="element-5111" data-mp="16tw82hcreo4y">
            Submit ticket
          </Button>
        </>} data-id="element-5109" data-mp="a5q7c5iykfvy">
      <div className="space-y-5" data-id="element-5112" data-mp="28knx6sm7kagr">
        <Field label="What is this about?" htmlFor="ticket-category" data-id="element-5113" data-mp="28jmafqvtxzj7">
          <Select id="ticket-category" value={category} onChange={event => {
          setCategory(event.target.value as TicketCategory);
          setReference('');
        }} data-id="element-5114" data-mp="23mco4k3qm3zl">
            {ticketCategories.map(option => <option key={option} value={option} data-id="element-5115" data-mp="haviopuf0cjn">
                {option}
              </option>)}
          </Select>
        </Field>

        {options.length > 0 && <Field label="Which record?" htmlFor="ticket-reference" hint="Optional, but it gets you a faster answer." data-id="element-5116" data-mp="3kr2pv6c1grx6">
            <Select id="ticket-reference" value={reference} onChange={event => setReference(event.target.value)} data-id="element-5117" data-mp="3uogrhztazc5f">
              <option value="" data-id="element-5118" data-mp="2lzfp777tppw">Not about a specific record</option>
              {options.map(option => <option key={option.reference} value={option.reference} data-id="element-5119" data-mp="1a0auvixtgbh5">
                  {option.label} ({option.reference})
                </option>)}
            </Select>
          </Field>}

        <Field label="Subject" htmlFor="ticket-subject" data-id="element-5120" data-mp="2ug8y13x4618f">
          <Input id="ticket-subject" value={subject} onChange={event => setSubject(event.target.value)} placeholder="One line — what went wrong" data-id="element-5121" data-mp="2ugsiara8l1pm" />
        </Field>

        <Field label="What happened?" htmlFor="ticket-body" hint="Dates, amounts and names help. Never include your password — no agent will ask for it." data-id="element-5122" data-mp="2kocyrpjak52m">
          <Textarea id="ticket-body" rows={5} value={body} onChange={event => setBody(event.target.value)} data-id="element-5123" data-mp="2audzqzeil1ew" />
        </Field>

        {isReport && <div className="border-t border-line pt-4" data-id="element-5124" data-mp="21txy7vhc04co">
            <Toggle checked={confidential} onChange={setConfidential} label="Do not tell them who reported this" description="They are told a report was made and what about, never by whom. It may limit what we can ask them." data-id="element-5125" data-mp="4y9vlhirprgm" />
          </div>}

        {category === 'Suspicious activity' && <p className="rounded-md border border-danger-100 bg-danger-50 px-3 py-2.5 text-body-sm text-ink" data-id="element-5126" data-mp="2vayxs67mhxbl">
            This goes to trust and safety immediately and is treated as urgent. If you are in
            physical danger, use the emergency alert on the Safety tab instead.
          </p>}
      </div>
    </Modal>;
}